import { ArrowDownLeft, ArrowUpRight } from 'lucide-react';

interface PaymentSplitInfoProps {
  amount: number;
  platformCommission?: number;
  creatorEarnings?: number;
  currency?: string;
  direction?: 'incoming' | 'outgoing';
  compact?: boolean;
}

/**
 * Component to display payment split breakdown (30% platform / 70% creator)
 */
export const PaymentSplitInfo = ({
  amount,
  platformCommission,
  creatorEarnings,
  currency = 'RWF',
  direction = 'incoming',
  compact = false,
}: PaymentSplitInfoProps) => {
  // Calculate split if not provided
  const calculatedPlatformCommission = platformCommission ?? Math.round(amount * 0.3);
  const calculatedCreatorEarnings = creatorEarnings ?? amount - calculatedPlatformCommission;

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-RW', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (compact) {
    return (
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          {direction === 'incoming' ? (
            <ArrowDownLeft className="h-4 w-4 text-emerald-600" />
          ) : (
            <ArrowUpRight className="h-4 w-4 text-orange-600" />
          )}
          <span className="text-sm font-medium text-slate-700">Platform: {formatCurrency(calculatedPlatformCommission)}</span>
        </div>
        <span className="text-xs text-slate-400">•</span>
        <span className="text-sm font-medium text-slate-700">Creator: {formatCurrency(calculatedCreatorEarnings)}</span>
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-slate-900">Payment Split</h4>
        <span className="text-xs font-medium text-slate-500">30% / 70%</span>
      </div>

      <div className="space-y-2">
        {/* Platform Commission */}
        <div className="flex items-center justify-between rounded-lg bg-red-50 p-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100">
              <span className="text-xs font-bold text-red-700">P</span>
            </div>
            <div>
              <p className="text-xs text-slate-600">Platform Commission</p>
              <p className="text-sm font-semibold text-red-700">30%</p>
            </div>
          </div>
          <p className="text-base font-bold text-red-700">{formatCurrency(calculatedPlatformCommission)}</p>
        </div>

        {/* Creator Earnings */}
        <div className="flex items-center justify-between rounded-lg bg-emerald-50 p-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100">
              <span className="text-xs font-bold text-emerald-700">C</span>
            </div>
            <div>
              <p className="text-xs text-slate-600">Creator Earnings</p>
              <p className="text-sm font-semibold text-emerald-700">70%</p>
            </div>
          </div>
          <p className="text-base font-bold text-emerald-700">{formatCurrency(calculatedCreatorEarnings)}</p>
        </div>
      </div>

      {/* Total */}
      <div className="border-t border-slate-200 pt-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-slate-900">Total</p>
          <p className="text-base font-bold text-slate-900">{formatCurrency(amount)}</p>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          ✓ {formatCurrency(calculatedPlatformCommission)} + {formatCurrency(calculatedCreatorEarnings)} = {formatCurrency(amount)}
        </p>
      </div>
    </div>
  );
};

export default PaymentSplitInfo;
